import React, { useState } from 'react';
import { Eye, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function PromptPreviewDialog({ agentId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [variantAPreview, setVariantAPreview] = useState(null);
  const [variantBPreview, setVariantBPreview] = useState(null);
  const [copied, setCopied] = useState(null);

  const loadPreviews = async () => {
    setLoading(true);
    try {
      const [variantA, variantB] = await Promise.all([
        api.get(`/agents/${agentId}/prompt-preview?variant=A`),
        api.get(`/agents/${agentId}/prompt-preview?variant=B`),
      ]);
      setVariantAPreview(variantA.data);
      setVariantBPreview(variantB.data);
    } catch (error) {
      toast.error('Failed to load prompt preview');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      loadPreviews();
    }
  };

  const copyToClipboard = async (text, variant) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(variant);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(variant);
        toast.success('Copied to clipboard!');
        setTimeout(() => setCopied(null), 2000);
      } catch (err) {
        toast.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="preview-prompt-button">
          <Eye className="w-4 h-4 mr-2" />
          Preview Prompt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>System Prompt Preview</DialogTitle>
          <DialogDescription>
            View the exact prompt sent to the AI (Variant A vs Variant B)
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="A" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="A">Variant A (Guardrails First)</TabsTrigger>
              <TabsTrigger value="B">Variant B (Context First)</TabsTrigger>
            </TabsList>
            <TabsContent value="A" className="space-y-4">
              {variantAPreview && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">
                        {variantAPreview.character_count} characters
                      </Badge>
                      <Badge variant="secondary">
                        {variantAPreview.line_count} lines
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(variantAPreview.system_prompt, 'A')}
                      className="rounded-full"
                    >
                      {copied === 'A' ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied === 'A' ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 font-mono text-sm whitespace-pre-wrap max-h-96 overflow-y-auto border">
                    {variantAPreview.system_prompt}
                  </div>
                </>
              )}
            </TabsContent>
            <TabsContent value="B" className="space-y-4">
              {variantBPreview && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">
                        {variantBPreview.character_count} characters
                      </Badge>
                      <Badge variant="secondary">
                        {variantBPreview.line_count} lines
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(variantBPreview.system_prompt, 'B')}
                      className="rounded-full"
                    >
                      {copied === 'B' ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied === 'B' ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 font-mono text-sm whitespace-pre-wrap max-h-96 overflow-y-auto border">
                    {variantBPreview.system_prompt}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
